export interface CRMPipelineStage {
  id: string;
  name: string;
  description: string | null;
  order_index: number;
  color: string | null;
  default_probability: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Fédérations professionnelles du transport de voyageurs.
 * Miroir de la contrainte `crm_accounts_federation_known` en base : toute valeur
 * ajoutée ici doit l'être aussi dans la contrainte, sinon l'écriture est rejetée.
 */
export const ACCOUNT_FEDERATIONS = ['FNTV', 'OTRE', 'FNTR', 'aucune'] as const;
export type AccountFederation = (typeof ACCOUNT_FEDERATIONS)[number];

/**
 * Comment l'adhésion a été établie, du plus fort au plus faible.
 * Miroir de `crm_accounts_federation_source_known`.
 */
export const ACCOUNT_FEDERATION_SOURCES = [
  'coupon_stripe',        // le compte porte un coupon réservé aux adhérents — preuve la plus forte
  'repertoire_fntv',      // repertoire.fntv.fr (art. 2.2 du partenariat)
  'fntv_services',        // confirmé par la FNTV Services (art. 2.2)
  'declaration_prospect', // l'entreprise l'a déclaré elle-même (mail, transcript de réunion)
  'transcript',           // déduit d'un transcript de réunion
  'manuel',
] as const;
export type AccountFederationSource = (typeof ACCOUNT_FEDERATION_SOURCES)[number];

/** Libellés courts pour l'IHM — l'origine de l'info compte autant que l'info. */
export const ACCOUNT_FEDERATION_SOURCE_LABELS: Record<AccountFederationSource, string> = {
  coupon_stripe: 'coupon adhérent',
  repertoire_fntv: 'répertoire FNTV',
  fntv_services: 'confirmé par la FNTV Services',
  declaration_prospect: "déclaré par l'entreprise",
  transcript: 'transcript de réunion',
  manuel: 'saisi manuellement',
};

/**
 * Narrowing frontière DB → domaine. La colonne `crm_accounts.federation` est du
 * `text` gardé par la contrainte CHECK (le vrai garde-fou) ; les types générés
 * la sortent donc `string | null`. On borne ici — une seule définition pour tous
 * les normaliseurs, pas un cast `as` par surface. Une valeur hors vocabulaire
 * (impossible tant que la contrainte tient) dégrade en `null` (= non vérifié)
 * plutôt que de mentir au type.
 */
export function toAccountFederation(value: string | null | undefined): AccountFederation | null {
  return (ACCOUNT_FEDERATIONS as readonly string[]).includes(value ?? '')
    ? (value as AccountFederation)
    : null;
}

/** Même narrowing pour `federation_source` (contrainte `crm_accounts_federation_source_known`). */
export function toAccountFederationSource(
  value: string | null | undefined,
): AccountFederationSource | null {
  return (ACCOUNT_FEDERATION_SOURCES as readonly string[]).includes(value ?? '')
    ? (value as AccountFederationSource)
    : null;
}

export interface CRMAccount {
  id: string;
  company_id?: string | null;
  name: string;
  industry: string | null;
  website: string | null;
  logo_url: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  siret: string | null;
  siren: string | null;
  naf_code: string | null;
  phone: string | null;
  /**
   * Rôle DÉRIVÉ du revenu (vue `v_account_role`), fusionné au fetch des comptes.
   * Remplace l'ancien champ `status` (tag manuel pollué, supprimé). Le compte
   * n'a pas de statut commercial en propre — cf. PLAN-ACCOUNT-MODEL.
   */
  role: 'client' | 'former_client' | 'prospect';
  clientSource?: 'stripe' | 'invoice' | null;
  invoiceCadence?: string | null;
  nextRenewalAt?: string | null;
  /**
   * @deprecated Statut de COMPAT, DÉRIVÉ du rôle au fetch (client→customer,
   * former→churned, prospect). La colonne DB `crm_accounts.status` est supprimée.
   * Pour tout nouveau code, utiliser `role`. Conservé pour les lecteurs legacy.
   */
  status?: 'prospect' | 'customer' | 'churned';
  notes: string | null;
  owner_id: string | null;
  parent_account_id: string | null;
  custom_fields: Record<string, unknown>;
  /** Coordinates (WGS84), set by the crm-geocode Edge Function. */
  lat: number | null;
  lng: number | null;
  geocoded_at: string | null;
  /**
   * Profil autocariste — enrichissement externe (ex. classement Car&Bus News) ou
   * saisie sales. Tous nullables ; `data_source` trace la provenance.
   */
  region?: string | null;
  /** Nombre d'autocars/bus (licences LC). */
  fleet_size?: number | null;
  /** Nombre de VL < 9 places (licences LTI). */
  fleet_light_vehicles?: number | null;
  annual_revenue_eur?: number | null;
  revenue_year?: number | null;
  net_result_eur?: number | null;
  /** Logiciel d'exploitation / TMS (Gescar, ABC Car, Ordicar, Perinfo, Planingo, Waybe…). */
  tms?: string | null;
  /** Groupement d'achat/marketing (Réunir, AGIR…) — distinct de la maison-mère. */
  groupement?: string | null;
  /**
   * Fédération professionnelle. `null` = non vérifié, `'aucune'` = non-adhérent confirmé.
   * À ne pas confondre avec `groupement`, qui est un groupement d'achat.
   * Conditionne le tarif partenaire FNTV : source de vérité = repertoire.fntv.fr,
   * ou vérification au cas par cas auprès de la FNTV Services (art. 2.2 du partenariat).
   */
  federation?: AccountFederation | null;
  /** D'où vient l'information d'adhésion — une adhésion non tracée n'est pas opposable. */
  federation_source?: AccountFederationSource | null;
  /** Dernière vérification. Une adhésion se perd : au-delà d'un an, revérifier. */
  federation_checked_at?: string | null;
  data_source?: string | null;
  created_at: string;
  updated_at: string;
}

/** Node de l'arbre des comptes retourne par l'API crmListAccountTree. */
export interface AccountTreeNode {
  id: string;
  parent_account_id: string | null;
  depth: number;
  name: string;
  /** Statut DÉRIVÉ renvoyé par l'Edge crmListAccountTree (compat). */
  status?: 'prospect' | 'customer' | 'churned';
  industry: string | null;
  updated_at: string;
}

export interface ContactEmail {
  id: string;
  contact_id: string;
  email: string;
  label: string;
  is_primary: boolean;
  created_at: string;
}

export interface ContactPhone {
  id: string;
  contact_id: string;
  phone: string;
  label: string;
  is_primary: boolean;
  created_at: string;
}

export interface ContactAccount {
  id: string;
  contact_id: string;
  account_id: string;
  is_primary: boolean;
  role: string | null;
  created_at: string;
  account?: CRMAccount;
}

export interface CRMContact {
  id: string;
  account_id: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  status: 'active' | 'inactive';
  owner_id: string | null;
  last_contacted_at: string | null;
  feature_notifications_enabled: boolean;
  custom_fields: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Multi-value relations (loaded via joins)
  emails?: ContactEmail[];
  phones?: ContactPhone[];
  contact_accounts?: ContactAccount[];
}

export interface CRMOpportunity {
  id: string;
  name: string;
  account_id: string;
  stage_id: string | null;
  amount: number | null;
  currency: string;
  close_date: string | null;
  probability: number | null;
  status: 'open' | 'won' | 'lost';
  owner_id: string | null;
  custom_fields: Record<string, unknown>;
  onboarding_lead_id: string | null;
  is_onboarding: boolean;
  stripe_product_id: string | null;
  custom_product_name: string | null;
  calculated_mrr: number | null;
  calculated_arr: number | null;
  revenue_type: 'recurring' | 'one_time' | 'mixed';
  one_time_amount: number | null;
  target_close_date: string | null;
  reason_lost: string | null;
  created_at: string;
  updated_at: string;
  account?: CRMAccount;
  stage?: CRMPipelineStage;
  contacts?: CRMContact[];
  stripe_product?: StripeProduct;
}

export interface MRRSnapshot {
  id: string;
  snapshot_date: string;
  mrr_actual: number;
  mrr_forecast: number;
  arr_actual: number;
  opportunities_open: number;
  opportunities_won: number;
  opportunities_lost: number;
  pipeline_value: number;
  one_time_revenue: number;
  conversion_rate: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface StripeProduct {
  id: string;
  stripe_product_id: string | null;
  stripe_price_id: string;
  name: string;
  description: string | null;
  unit_amount: number;
  currency: string;
  interval: 'month' | 'year' | 'one_time';
  interval_count: number;
  is_active: boolean;
  product_type: 'subscription' | 'one_time';
  metadata: Record<string, unknown>;
  livemode?: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface StripeSubscriptionInfo {
  subscription_id: string;
  customer_id: string;
  status: 'active' | 'trialing' | 'canceled' | 'past_due' | 'not_started';
  price_id: string | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  discount_percent: number | null;
  discount_amount: number | null;
}

export interface LeadProduct {
  id: string;
  lead_id: string;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  custom_product_name: string | null;
  custom_unit_amount: number | null;
  custom_interval: 'month' | 'year' | 'one_time' | null;
  quantity: number;
  status: 'active' | 'inactive' | 'pending';
  notes: string | null;
  created_at: string;
  updated_at: string;
  product?: StripeProduct;
}

export interface CRMActivity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'note' | 'task';
  title: string;
  description: string | null;
  related_to_type: 'account' | 'opportunity' | 'contact' | 'lead';
  related_to_id: string;
  performed_by: string | null;
  performed_at: string;
  created_at: string;
}

// CRMTask is canonically defined in ./tasks (richer status/priority unions
// matching the UI labels; the DB column is an unconstrained string).
// Re-exported here so CRM consumers keep a single import point.
export type { CRMTask, TaskStatus, TaskPriority } from './tasks';

export interface OnboardingLead {
  id: string;
  company_id?: string | null;
  company_name: string;
  siret: string;
  address: string;
  postal_code: string;
  city: string;
  country: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  erp: string;
  erp_other: string | null;
  referral: string;
  referral_other: string | null;
  slot_start: string | null;
  slot_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  selected_plan: string | null;
  status: string;
  user_id: string | null;
  // No `rdvN_start/_end`: that booking-time copy lies once a meeting moves. The real slot of
  // the journey meetings comes from `v_onboarding_lead_rdv_slots` (useOnboardingRdvSlots).
  crm_opportunity_id: string | null;
  /** FK canonique vers le compte CRM (Phase 1 modèle Compte=Entreprise). */
  account_id: string | null;
  /** Jalon de churn posé par le webhook Stripe à l'annulation d'abonnement. */
  churned_at: string | null;
  current_onboarding_stage: number;
  onboarding_type: 'standard' | 'pilote' | 'grand_compte';
  first_value_achieved_at: string | null;
  go_live_at: string | null;
  internal_notes: string | null;
  owner_id: string | null;
  /** Coordinates (WGS84), set by the crm-geocode Edge Function. */
  lat: number | null;
  lng: number | null;
  geocoded_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OnboardingChecklist {
  id: string;
  lead_id: string;
  checklist_type: 'client' | 'internal';
  created_at: string;
  updated_at: string;
  items?: OnboardingChecklistItem[];
}

export interface OnboardingChecklistItem {
  id: string;
  checklist_id: string;
  title: string;
  description: string | null;
  order_index: number;
  is_completed: boolean;
  completed_at: string | null;
  completed_by: string | null;
  is_required: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface OnboardingDocument {
  id: string;
  lead_id: string;
  doc_type: 'logo' | 'cgv' | 'tarifs' | 'devis_type' | 'other';
  file_name: string;
  file_url: string;
  file_size: number | null;
  mime_type: string | null;
  status: 'pending' | 'approved' | 'rejected';
  notes: string | null;
  uploaded_by: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OnboardingStageHistory {
  id: string;
  lead_id: string;
  stage_number: number;
  stage_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  started_at: string | null;
  completed_at: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export type KanbanCardData = {
  id: string;
  type: 'opportunity' | 'lead' | 'account';
  title: string;
  subtitle: string;
  amount?: number;
  stage_id: string;
  status: string;
  contact_name?: string;
  contact_email?: string;
  created_at: string;
  updated_at: string;
  is_onboarding: boolean;
  onboarding_stage?: number;
  stripe_subscription?: StripeSubscriptionInfo;
  has_active_subscription: boolean;
  mrr?: number;
  arr?: number;
  raw_data: CRMOpportunity | OnboardingLead | CRMAccount;
};

export const ONBOARDING_STAGES = [
  { number: 1, name: 'Activation', description: 'Paiement validé, début onboarding' },
  { number: 2, name: 'Préparation client', description: 'Questionnaire, documents, vidéos' },
  { number: 3, name: 'Session paramétrage', description: 'Call 1 - Configuration guidée' },
  { number: 4, name: 'First Value', description: 'Premier succès autonome' },
  { number: 5, name: 'Phase tests + J+7', description: 'Tests réels et point J+7' },
  { number: 6, name: 'Go Live', description: 'Production lancée' },
  { number: 7, name: 'Stabilisation J+30', description: 'Suivi et ajustements finaux' },
  // Hors du kanban par défaut : l'onboarding est terminé (cf. src/lib/onboardingStage.ts).
  { number: 8, name: 'Stable', description: 'Onboarding terminé : client autonome, hors du kanban' },
] as const;

export interface ClientAccount {
  id: string;
  external_auth_id: string;
  email: string;
  display_name: string | null;
  lead_id: string | null;
  company_id: string | null;
  stripe_customer_id: string | null;
  billing_access: boolean;
  is_active: boolean;
  referral_code: string | null;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined relations
  lead?: { id: string; account?: { id: string; name: string } | null; company_name: string; contact_email: string; status: string } | null;
  company?: { id: string; name: string } | null;
}

export const CLIENT_CHECKLIST_TEMPLATE = [
  { title: 'Questionnaire complété', description: 'Raison sociale, contact, ERP, etc.', is_required: true },
  { title: 'Logo HD fourni', description: 'Format PNG ou SVG, haute résolution', is_required: true },
  { title: 'CGV fournies', description: 'Conditions générales de vente', is_required: true },
  { title: 'Grilles tarifaires envoyées', description: 'Tarifs complets et à jour', is_required: true },
  { title: 'Devis types fournis', description: '3 à 5 devis représentatifs', is_required: true },
  { title: 'Vidéos visionnées', description: 'Vision, utilisation, préparation', is_required: true },
  { title: 'Tests réalisés', description: 'Au moins un test envoyé', is_required: false },
  { title: 'Session paramétrage effectuée', description: 'Call 1 complété', is_required: true },
  { title: 'Go Live validé', description: 'Production approuvée', is_required: true },
];

export const INTERNAL_CHECKLIST_TEMPLATE = [
  { title: 'Vérification tarifs', description: 'Tarifs importés et vérifiés', is_required: true },
  { title: 'Paramétrage règles', description: 'Règles métier configurées', is_required: true },
  { title: 'Tests validés', description: 'Devis de test conformes', is_required: true },
  { title: 'Modèles devis OK', description: 'Templates personnalisés', is_required: true },
  { title: 'ERP connecté', description: 'Intégration Gescar/ABC/autre', is_required: false },
  { title: 'Email connecté', description: 'Réception emails configurée', is_required: true },
  { title: 'Go Live validé', description: 'Passage en production approuvé', is_required: true },
  { title: 'Stabilisation faite', description: 'Bilan J+30 effectué', is_required: true },
];
